/**
 * zoomAwareModifier — @dnd-kit modifier that compensates for CSS `zoom`.
 *
 * CSS `zoom` scales element coordinates from `getBoundingClientRect()` but
 * DragOverlay renders via a portal at body level (outside the zoomed container).
 * This causes the overlay to be offset — proportional to the zoom level.
 *
 * The fix: divide the overlay transform by the effective zoom factor.
 *
 * Usage:
 *   <DragOverlay modifiers={[makeZoomModifier('--content-zoom')]}>
 *   <DragOverlay modifiers={[makeZoomModifier('--menu-zoom')]}>
 *
 * @see useZoomEffect.ts — sets --menu-zoom and --content-zoom on :root
 */

import type { Modifier } from "@dnd-kit/core"

/**
 * Create a modifier that compensates for CSS zoom on a given CSS variable.
 *
 * @param cssVar — CSS custom property name (e.g. '--content-zoom')
 * @param extraModifier — optional extra transform (e.g. restrict axis)
 */
export function makeZoomModifier(
  cssVar: string,
  extraModifier?: Modifier,
): Modifier {
  return (args) => {
    const { transform } = args
    const zoom =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(cssVar),
      ) || 1

    // Apply extra modifier first (e.g. restrictToVerticalAxis)
    const base = extraModifier ? extraModifier(args) : transform

    // When zoom > 1, getBoundingClientRect returns inflated values.
    // The DragOverlay is outside the zoom container, so we need to
    // scale the transform down to match the actual visual position.
    if (zoom === 1) return base

    return {
      ...base,
      x: base.x / zoom,
      y: base.y / zoom,
    }
  }
}

/** Pre-built modifier for content area zoom */
export const contentZoomModifier: Modifier = makeZoomModifier("--content-zoom")

/** Pre-built modifier for sidebar/menu zoom */
export const menuZoomModifier: Modifier = makeZoomModifier("--menu-zoom")

/**
 * Sidebar modifier: restrict to vertical axis + compensate for menu zoom.
 */
export const sidebarDndModifier: Modifier = makeZoomModifier(
  "--menu-zoom",
  ({ transform }) => ({ ...transform, x: 0 }),
)
